import { ChevronRight } from '@/assets/icons'
import { DEVICE_TYPE, useDeviceType } from '@/hooks/useDeviceType'
import { useNavigate, useParams } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { RootState } from '@/store/store'
import { kUntitledMatterLabel } from '@/matter/constants'
import { kMyDriveName, matterPaths } from '@/routing/paths'
import { selectMatterById } from '@/matter/store/selectors'
import { useAppSelector } from '@/store/store-hooks'
import { MatterRead } from '@/matter/store/schemas'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'

type BreadcrumbItem = {
  label: string
  path: string
}

const createBreadcrumbItems = (path: string[], matter: MatterRead | null): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = []

  // If we have a matter, use matter name as the first breadcrumb label
  // The matter ID is used in all paths for navigation
  if (matter) {
    // Matter root breadcrumb - path is the matter ID folder
    breadcrumbs.push({
      label: matter.name ?? kUntitledMatterLabel,
      path: matter.id,
    })

    // Subfolders - paths include matter ID prefix (e.g., "{matterId}/Documents", "{matterId}/Documents/Contracts")
    let relativePath = matter.id
    for (let i = 1; i < path.length; i++) {
      const segment = path[i]
      relativePath = `${relativePath}/${segment}`
      breadcrumbs.push({
        label: segment,
        path: relativePath,
      })
    }
  } else {
    // No matter - fallback to normal breadcrumb generation
    let fullPath = ''
    for (const segment of path) {
      fullPath = fullPath ? `${fullPath}/${segment}` : segment
      breadcrumbs.push({
        label: segment,
        path: fullPath,
      })
    }
  }

  return breadcrumbs
}

/**
 * Create breadcrumb items for unassigned matter.
 * Shows "Unassigned" as root, then folder path without matter ID prefix.
 */
const createUnassignedBreadcrumbItems = (path: string[]): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = []

  // Unassigned root breadcrumb - empty path navigates to drive root
  breadcrumbs.push({
    label: 'Unassigned',
    path: '',
  })

  // Subfolders - paths are just folder names (e.g., "Documents", "Documents/Contracts")
  let relativePath = ''
  for (const segment of path) {
    relativePath = relativePath ? `${relativePath}/${segment}` : segment
    breadcrumbs.push({
      label: segment,
      path: relativePath,
    })
  }

  return breadcrumbs
}

type BreadcrumbsProps = {
  folderPath: string
}

export default function Breadcrumbs({ folderPath }: BreadcrumbsProps) {
  // Get matter context from URL params
  const { matterId: urlMatterId, driveName } = useParams<{ matterId: string; driveName: string }>()

  // urlMatter: The matter from URL params - used to determine if we're in unassigned context
  // This drives the breadcrumb generation strategy (unassigned vs regular matter paths)
  const urlMatter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: urlMatterId })) ?? null
  const isUnassigned = isUnassignedMatter(urlMatter)

  // Parse the storage path to extract folder segments
  const pathSegments = folderPath.split('/').filter(Boolean)
  const trimmedPath = pathSegments.slice(2) // removes 'user' and 'user_id' from storage URL

  // matter: Derived from folder path's first segment - used for breadcrumb label generation
  // This is the matter whose folder we're inside (path[0] is the matter ID in storage)
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: trimmedPath[0] })) ?? null

  // Build breadcrumbs based on context:
  // - For unassigned matter: show "Unassigned" as root, then folder path (no matter ID in paths)
  // - For regular matter: show matter name as root, with matter ID in all paths
  const breadcrumbs = isUnassigned ? createUnassignedBreadcrumbItems(trimmedPath) : createBreadcrumbItems(trimmedPath, matter)

  const deviceType = useDeviceType()
  const shouldTruncate = deviceType !== DEVICE_TYPE.DESKTOP ? breadcrumbs.length >= 2 : breadcrumbs.length >= 5
  const navigate = useNavigate()

  // Build base drive location path using centralized path builder
  // urlMatterId is required since files routes always have matter context
  const baseDriveLocationPath = urlMatterId ? matterPaths.files.drive(urlMatterId, driveName ?? kMyDriveName) : '/dashboard/matters'

  const last = breadcrumbs[breadcrumbs.length - 1]

  const hidden = breadcrumbs.slice(0, -1)

  const handleBreadcrumbClick = (path: string) => {
    // Empty path = root (unassigned matter root), navigate to baseDriveLocationPath
    // Otherwise append the path to the base drive location
    if (path === '') {
      navigate(baseDriveLocationPath)
    } else {
      navigate(`${baseDriveLocationPath}/${path}`)
    }
  }

  return (
    <div className="inline-flex items-center overflow-hidden max-w-full">
      {shouldTruncate ? (
        <DropdownMenu.Root>
          <div className="inline-flex items-center">
            {/* Ellipsis dropdown */}
            <DropdownMenu.Trigger className="text-sm underline cursor-pointer text-nowrap text-ellipsis">..</DropdownMenu.Trigger>
            {/* Dropdown menu with hidden breadcrumbs */}
            <DropdownMenu.Portal>
              <DropdownMenu.Content className={`z-50 rounded-md border border-brand-neutral-200 bg-white p-2 text-sm shadow-lg max-w-[260px]`}>
                {hidden.map((item) => (
                  <DropdownMenu.Item
                    key={item.path}
                    className="flex items-center px-3 py-2 cursor-pointer rounded text-brand-neutral-700 font-semibold hover:bg-brand-neutral-100 hover:text-brand-500 focus:outline-none focus:bg-brand-neutral-100 focus:text-brand-500"
                    onClick={() => handleBreadcrumbClick(item.path)}
                  >
                    <span className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap">{item.label}</span>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
            <ChevronRight className="mx-1 text-brand-neutral-900 w-4 h-4" />
            {/* Last breadcrumb (current folder) */}
            <span
              className={`font-semibold text-sm text-brand-neutral-900 cursor-default text-nowrap overflow-hidden text-ellipsis whitespace-nowrap ${
                deviceType !== DEVICE_TYPE.DESKTOP && 'max-w-[160px]'
              }`}
              title={last.label}
            >
              {last.label}
            </span>
          </div>
        </DropdownMenu.Root>
      ) : (
        // shouldTruncate is false - Only for desktop
        <div className="inline-flex items-center overflow-hidden max-w-full">
          {breadcrumbs.map((breadcrumb, index) => (
            <div key={index} className="inline-flex items-center">
              <button
                onClick={() => handleBreadcrumbClick(breadcrumb.path)}
                className={`font-semibold text-sm text-brand-neutral-900 text-nowrap overflow-hidden text-ellipsis whitespace-nowrap ${
                  index === breadcrumbs.length - 1 ? 'cursor-default' : 'underline cursor-pointer'
                } ${deviceType !== DEVICE_TYPE.DESKTOP && 'max-w-[160px]'}`}
                title={breadcrumb.label}
              >
                {breadcrumb.label}
              </button>
              {index < breadcrumbs.length - 1 && <ChevronRight className="mx-1 text-brand-neutral-900 w-4 h-4" />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
